import { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { CheckCircle } from 'lucide-react';
import { ERP_PROVIDERS, OnboardingConfig } from '@/types/onboarding';
import { Button } from '@components/common/Button';
import {
  ERP_AUTH_PROVIDERS,
  type ErpConnectionCredentials,
  getErpProviderLabel,
  requestErpAuthorization,
  type ErpAuthProvider,
} from '@api/quickbooksAuth';
import { useNotificationStore } from '@store/notificationStore';
import { InfoTooltip } from '@components/common/InfoTooltip';

interface ErpIntegrationStepProps {
  config: OnboardingConfig['erp_integration'] | undefined;
  onChange: (config: OnboardingConfig['erp_integration'] | undefined) => void;
  onSaveStateForOAuth: () => void;
}

export const ErpIntegrationStep = ({
  config,
  onChange,
  onSaveStateForOAuth,
}: ErpIntegrationStepProps) => {
  const [selectedProvider, setSelectedProvider] = useState<string | null>(
    config?.erp_type ?? null
  );
  const [ajeraCredentials, setAjeraCredentials] = useState<ErpConnectionCredentials>({});
  const [isConnecting, setIsConnecting] = useState(false);
  const { addNotification } = useNotificationStore();
  const isSelectedProviderSupported = ERP_AUTH_PROVIDERS.includes(
    selectedProvider as ErpAuthProvider
  );
  const selectedAuthProvider = isSelectedProviderSupported
    ? selectedProvider as ErpAuthProvider
    : null;
  const isAjeraSelected = selectedAuthProvider === 'ajera';

  // When config becomes connected (after OAuth return), sync provider selection
  useEffect(() => {
    if (config?.is_connected && config.erp_type) {
      setSelectedProvider(config.erp_type);
    }
  }, [config]);

  const handleProviderClick = (providerKey: string, available: boolean) => {
    if (!available) return;
    const next = selectedProvider === providerKey ? null : providerKey;
    setSelectedProvider(next);
    if (!next) onChange(undefined);
  };

  const handleConnect = async () => {
    if (!selectedAuthProvider) {
      addNotification({
        type: 'error',
        message: 'Select an ERP provider first.',
      });
      return;
    }

    const provider = selectedAuthProvider;
    const providerLabel = getErpProviderLabel(provider);
    if (provider === 'ajera') {
      const missingApiId = !(ajeraCredentials.api_url || '').includes('?') && !ajeraCredentials.api_id;
      if (!ajeraCredentials.api_url || !ajeraCredentials.username || !ajeraCredentials.password || missingApiId) {
        addNotification({
          type: 'error',
          message: 'Enter the Ajera API URL, API ID, username, and password.',
        });
        return;
      }
    }

    setIsConnecting(true);
    try {
      const { is_authenticated, authorization_url } = await requestErpAuthorization(provider, {
        credentials: provider === 'ajera' ? ajeraCredentials : undefined,
      });

      if (is_authenticated) {
        // Already connected (e.g. returning from another session)
        onChange({ erp_type: provider, is_connected: true });
        return;
      }

      if (authorization_url) {
        // Save wizard state before leaving the page
        onSaveStateForOAuth();
        window.location.href = authorization_url;
      } else {
        addNotification({
          type: 'error',
          message: `${providerLabel} is not configured. Please contact support.`,
        });
      }
    } catch {
      addNotification({
        type: 'error',
        message: `Failed to initiate ${providerLabel} connection.`,
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const isConnected = Boolean(config?.is_connected);

  return (
    <div className="space-y-6">
      {/* Heading */}
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">ERP Integration</h2>
          <InfoTooltip>Connect the accounting system used for AP posting.</InfoTooltip>
        </div>
      </div>

      {/* Provider selection grid */}
      <div className="grid grid-cols-2 gap-4">
        {ERP_PROVIDERS.filter((provider) => provider.available).map((provider) => (
          <button
            key={provider.key}
            type="button"
            onClick={() => handleProviderClick(provider.key, provider.available)}
            className={clsx(
              'p-4 rounded-lg border text-center transition-colors',
              'cursor-pointer hover:border-primary-400 dark:hover:border-primary-500',
              selectedProvider === provider.key
                ? 'border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
            )}
          >
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {provider.label}
            </span>
          </button>
        ))}
      </div>

      {/* ERP OAuth connect area */}
      {selectedAuthProvider && (
        <div className="p-4 rounded-lg border bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
          {isConnected ? (
            <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">{getErpProviderLabel(selectedAuthProvider)} connected</span>
            </div>
          ) : (
            <div className="space-y-3">
              {isAjeraSelected && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <input
                    type="url"
                    value={ajeraCredentials.api_url ?? ''}
                    onChange={(event) => setAjeraCredentials((prev) => ({ ...prev, api_url: event.target.value }))}
                    placeholder="Ajera API URL"
                    className="input"
                    autoComplete="off"
                  />
                  <input
                    type="text"
                    value={ajeraCredentials.api_id ?? ''}
                    onChange={(event) => setAjeraCredentials((prev) => ({ ...prev, api_id: event.target.value }))}
                    placeholder="API ID"
                    className="input"
                    autoComplete="off"
                  />
                  <input
                    type="text"
                    value={ajeraCredentials.username ?? ''}
                    onChange={(event) => setAjeraCredentials((prev) => ({ ...prev, username: event.target.value }))}
                    placeholder="Username"
                    className="input"
                    autoComplete="username"
                  />
                  <input
                    type="password"
                    value={ajeraCredentials.password ?? ''}
                    onChange={(event) => setAjeraCredentials((prev) => ({ ...prev, password: event.target.value }))}
                    placeholder="Password"
                    className="input"
                    autoComplete="current-password"
                  />
                </div>
              )}
              <Button
                variant="primary"
                onClick={handleConnect}
                disabled={isConnecting}
              >
                {isConnecting ? 'Connecting…' : `Connect to ${getErpProviderLabel(selectedAuthProvider)}`}
              </Button>
              <InfoTooltip>
                {isAjeraSelected
                  ? 'Ajera validates the API session directly.'
                  : `You will be redirected to ${getErpProviderLabel(selectedAuthProvider)} to approve the connection.`}
              </InfoTooltip>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
